import React, { useContext } from 'react';
import { Route, Routes } from 'react-router-dom';

import { useAccess } from '@/common/hooks/useAccess';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isAdmin, isManager, isUserEligibleForOverviewPage } from '../common/actionHelper';
import { NoraPermissionV2 } from '../common/constants/roles';
import { UserOrganization } from '../common/constants/userOrganization';
import { FeatureFlagsContext } from '../common/featureFlagsWrapper';
import { RouteNotFound } from '../features/404/RouteNotFound';
import APSOrderDetails from '../features/aps/APSOrderDetails';
import { CaseAssignmentPage } from '../features/case-assignment';
import { ControlPanelPage } from '../features/control-panel';
import { DashboardV2 } from '../features/dashboard/DashboardV2';
import { Detail } from '../features/detail/Detail';
import { RuleResultsDetails } from '../features/detail/ruleResultDetails/RuleResultsDetails';
import { ETIAnswer } from '../features/detail/tasks/etiGenerator/containers/ETIAnswer';
import { ETIPreview } from '../features/detail/tasks/etiGenerator/containers/ETIPreview';
import { Layout } from '../features/layout/Layout';
import { RequiredAuth } from '../features/layout/SecureRoute';
import { AuthContext } from '../features/login/AuthWrapper';
import { Mu } from '../features/mu/Mu';
import { PeerReviewPage } from '../features/peer-review';
import { PPWBLanding } from '../features/PPWB/PPWBLanding';
import { PPWBPolicy } from '../features/PPWB/PPWBPolicy';
import PPWBPolicyEvidenceGraph from '../features/PPWB/PPWBPolicyEvidenceGraph';
import QeInterviewQa from '../features/qeInterviewQa/QeInterviewQa';
import { RbacManagerPage } from '../features/rbac-manager';
import { TaskErrorsDashboard } from '../features/taskErrorsDashboard/TaskErrorsDashboard';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const AppRoutes = () => {
  const auth = useContext(AuthContext);
  const isEthos = auth.userOrganization === UserOrganization.ETHOS;
  const { flags } = useContext(FeatureFlagsContext);
  const { isQeInterviewQaEnabled } = flags;
  const canAccessControlPanelAdminRoutes = useAccess({
    permission: NoraPermissionV2.VIEW_CONTROL_PANEL,
    legacyCheck: r =>
      isAdmin(r) ||
      (isManager(r) && flags.controlPanelUnderwritingManagerEnabled),
  });
  const canAccessRbacManager = useAccess({
    permission: NoraPermissionV2.VIEW_CONTROL_PANEL,
    legacyCheck: isAdmin,
  });

  const canAccessPPWB = useAccess({
    permission: 'ACCESS_PPWB',
  });

  const canViewOverview = useAccess({
    permission: NoraPermissionV2.VIEW_OVERVIEW,
    legacyCheck: isUserEligibleForOverviewPage,
  });

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <Routes>
        <Route element={<RequiredAuth />}>
          <Route path="/mu" element={<Mu />} />

          <Route path="/" element={<Layout />}>
            <Route index element={<DashboardV2 />} />
            {isEthos && canViewOverview && <Route path="overview" element={<DashboardV2 />} />}
            {isEthos && <Route path="qa" element={<DashboardV2 />} />}
            <Route path="applications/:id" element={<Detail />} />
            <Route
              path="applications/:id/piaRules"
              element={<RuleResultsDetails />}
            />
            <Route
              path="applications/:policyId/aps-details/:taskId"
              element={<APSOrderDetails />}
            />
            {isQeInterviewQaEnabled && (
              <Route path="qna/policy/:policyId" element={<QeInterviewQa />} />
            )}
            {canAccessControlPanelAdminRoutes && (
              <Route path="controlpanel" element={<ControlPanelPage />} />
            )}
            <Route path="tasks/errored" element={<TaskErrorsDashboard />} />
            {isEthos && canAccessPPWB && (
              <>
                <Route path="ppwb" element={<PPWBLanding />} />
                <Route path="ppwb/policy/:id" element={<PPWBPolicy />} />
                <Route
                  path="ppwb/policy/:policyId/decision/:decisionId/graph"
                  element={<PPWBPolicyEvidenceGraph />}
                />
              </>
            )}

            {canAccessControlPanelAdminRoutes && (
              <Route path="case-assignment" element={<CaseAssignmentPage />} />
            )}

            {canAccessControlPanelAdminRoutes && (
              <Route path="peer-review" element={<PeerReviewPage />} />
            )}
            {canAccessRbacManager && (
              <Route path="rbac" element={<RbacManagerPage />} />
            )}
            {isEthos && flags.isCaseReferralEnabled && (
              <Route
                path="referrals/action-required"
                element={<DashboardV2 />}
              />
            )}
            <Route path="eti/:taskId">
              <Route path="preview" element={<ETIPreview />} />
              <Route path="answer" element={<ETIAnswer />} />
            </Route>

            <Route path="*" element={<RouteNotFound />} />
          </Route>
        </Route>
      </Routes>
    </QueryClientProvider>
  );
};

export default AppRoutes;
